/**
 * Forms & Inspections API Layer.
 *
 * React Query hooks for form templates, submissions, and inspection
 * history. Handles cache invalidation for assets when inspections
 * update the asset's inspection tracking fields.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import type {
  FormSubmission,
  FormSubmissionCreate,
  FormTemplate,
  FormTemplateCreate,
} from "@/types/form";

export function useFormTemplates(
  entityType?: string,
  templateType?: string,
  category?: string,
) {
  return useQuery<FormTemplate[]>({
    queryKey: ["form-templates", entityType, templateType, category],
    queryFn: async () => {
      const sp = new URLSearchParams();
      if (entityType) sp.set("entity_type", entityType);
      if (templateType) sp.set("template_type", templateType);
      if (category) sp.set("category", category);
      const params = sp.toString() ? `?${sp}` : "";
      return (await apiClient.get(`/api/v1/forms/templates${params}`))
        .data as FormTemplate[];
    },
  });
}

export function useFormTemplate(templateId: string | undefined) {
  return useQuery<FormTemplate>({
    queryKey: ["form-templates", templateId],
    queryFn: async () =>
      (await apiClient.get(`/api/v1/forms/templates/${templateId}`))
        .data as FormTemplate,
    enabled: !!templateId,
  });
}

export function useCreateFormTemplate() {
  const qc = useQueryClient();
  return useMutation<FormTemplate, Error, FormTemplateCreate>({
    mutationFn: async (data) =>
      (await apiClient.post("/api/v1/forms/templates", data))
        .data as FormTemplate,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["form-templates"] }),
  });
}

export function useSubmitForm(templateId: string) {
  const qc = useQueryClient();
  return useMutation<FormSubmission, Error, FormSubmissionCreate>({
    mutationFn: async (data) =>
      (
        await apiClient.post(
          `/api/v1/forms/templates/${templateId}/submit`,
          data,
        )
      ).data as FormSubmission,
    onSuccess: (_result, variables) => {
      qc.invalidateQueries({
        queryKey: [
          "form-submissions",
          variables.entity_type,
          variables.entity_id,
        ],
      });
      // Also invalidate asset inspection tracking if this was an inspection.
      if (variables.asset_id) {
        qc.invalidateQueries({ queryKey: ["assets", variables.asset_id] });
        qc.invalidateQueries({
          queryKey: ["asset-inspections", variables.asset_id],
        });
      }
      if (variables.work_order_id) {
        qc.invalidateQueries({
          queryKey: ["wo-form-status", variables.work_order_id],
        });
      }
    },
  });
}

export function useFormSubmissions(
  entityType: string,
  entityId: string | undefined,
) {
  return useQuery<FormSubmission[]>({
    queryKey: ["form-submissions", entityType, entityId],
    queryFn: async () =>
      (
        await apiClient.get(
          `/api/v1/forms/submissions?entity_type=${entityType}&entity_id=${entityId}`,
        )
      ).data as FormSubmission[],
    enabled: !!entityId,
  });
}

export function useAssetInspections(assetId: string | undefined) {
  return useQuery<FormSubmission[]>({
    queryKey: ["asset-inspections", assetId],
    queryFn: async () =>
      (await apiClient.get(`/api/v1/forms/inspections/asset/${assetId}`))
        .data as FormSubmission[],
    enabled: !!assetId,
  });
}

export function useWOForms(woId: string | undefined) {
  return useQuery<FormTemplate[]>({
    queryKey: ["wo-forms", woId],
    queryFn: async () =>
      (await apiClient.get(`/api/v1/forms/work-order/${woId}/forms`))
        .data as FormTemplate[],
    enabled: !!woId,
  });
}

export function useWOFormCompletionStatus(woId: string | undefined) {
  return useQuery<Record<string, string | null>>({
    queryKey: ["wo-form-status", woId],
    queryFn: async () =>
      (
        await apiClient.get(
          `/api/v1/forms/work-order/${woId}/completion-status`,
        )
      ).data,
    enabled: !!woId,
  });
}
